import {Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Logger} from 'src/util/logger';
import {Address} from '../../gen/model/address';
import {AuthenticationChallenge} from '../../gen/model/authenticationChallenge';
import {UserForRegisterUser} from '../../gen/model/userForRegisterUser';
import ResponseUtil from '../../util/responseUtil';
import {AppConstants} from '../AppConstants';
import {CountriesService} from '../countries-service';
import {FormInputComponent} from '../form-input/form-input.component';
import {LoginBaseComponent} from '../login-base.component';
import {ParamsService} from '../params.service';
import {AlertHandler} from '../no-concurrent-alerts-handler';
import {cardFooterFeedbackTransition} from '../animations';
import LocationUtil, {getAppBaseHref} from '../../util/locationUtil';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {ConfigurationService} from '../configuration-service';
import {AppStateService, ParamsForRouteNav} from '../app-state.service';
import {marker as _} from '@biesbjerg/ngx-translate-extract-marker';

const cloneDeep = require('lodash.clonedeep');

interface Item {
  value: string;
  label: string;
}
@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    cardFooterFeedbackTransition,
  ],
})
export class RegisterComponent extends LoginBaseComponent implements OnInit, OnDestroy {
  fields;
  registerForm: FormGroup;
  email: string;
  emailAsReadOnly = false;
  countryList: Item[];

  private errorBody: any;
  private genericError = false;

  constructor(
    private appStateService: AppStateService,
    private countries: CountriesService,
    route: ActivatedRoute,
    router: Router,
    private logger: Logger,
    private alertHandler: AlertHandler,
    private paramsService: ParamsService,
    private translate: TranslateService,
    hostElement: ElementRef,
    configuration: ConfigurationService
  ) {
    super(route, router, hostElement, configuration);
    this.fields = cloneDeep(this.configuration.getProperties().registrationFields);
    const controls = {};
    for (let i = 0; i < this.fields.length; i += 1) {
      const validators = [];
      const f = this.fields[i];
      if (f.key !== 'acceptTsAndCs' && f.required) {
        validators.push(Validators.required);
      }
      if (f.key ===  'acceptTsAndCs') {
        validators.push(Validators.requiredTrue);
        controls[f.key] = new FormControl(false, { validators, updateOn: 'change' });
      } else if (f.key ===  'address-country' && f.required) {
        validators.push(Validators.pattern(AppConstants.NOT_NULL_PATTERN));
        controls['country'] = new FormControl(null, validators);
      } else {
        if (f.key ===  'password') {
          validators.push(Validators.pattern(configuration.getProperties().passwordValidation));
        } else if (f.key ===  'email') {
          validators.push(Validators.email);
        }
        controls[f.key.replace('address-', '')] = new FormControl('', validators);
      }
    }
    this.registerForm = new FormGroup(controls);
  }

  ngOnInit() {
    super.init();
    this.countries.alphabetical().subscribe(res => {
      this.countryList = res.map((n) => ({label: n.name, value: n.code}));
    });
    this.validateConfirmPassword = this.validateConfirmPassword.bind(this);
    this.registerForm.setValidators([this.validateConfirmPassword]);
    this.subscribeParamsState();
  }
  ngOnDestroy() {}

  isFieldInvalid(field: string): boolean {
    return this.hasError(field) || this.isInputInvalid(field);
  }

  isInputInvalid(field: string): boolean {
    if (field === 'confirmPassword') {
      return (this.registerForm.get(field).invalid
        || (this.registerForm.invalid && this.registerForm.errors && this.registerForm.errors.mustMatch))
        && this.registerForm.get(field).touched;
    } else {
      return this.registerForm.get(field).invalid && this.registerForm.get(field).touched;
    }
  }

  isFieldValid(field: string): boolean {
    return !this.hasError(field) && this.isInputValid(field);
  }

  isInputValid(field: string): boolean {
    if (field === 'confirmPassword') {
      return (this.registerForm.get(field).valid
        && (this.registerForm.valid || !this.registerForm.errors || !this.registerForm.errors.mustMatch))
        && this.registerForm.get(field).touched;
    } else {
      return this.registerForm.get(field).valid && this.registerForm.get(field).touched;
    }
  }

  hasError(field?: string, code?: string): boolean {
    let retValue = false;
    if (field) {
      if (this.errorBody &&
          this.errorBody.error &&
          this.errorBody.childErrors &&
          this.errorBody.childErrors[field]) {
        if (code) {
          if (this.errorBody.childErrors[field].error === code) {
            retValue = true;
          }
        } else {
          retValue = true;
        }
      }
    } else if (this.genericError) {
      retValue = true;
    }
    return retValue;
  }
  resolveFieldValueForIncludedField(field: string): string | undefined {
    let retVal: string|undefined;
    if (field && this.fields.findIndex((val) => val.key === field) >= 0) {
      retVal = this.registerForm.get(field.replace('address-', '')).value;
    }
    return retVal;
  }

  removeUndefined(obj: any): any {
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i += 1) {
      if (obj[keys[i]] === undefined) {
        delete obj[keys[i]];
      }
    }
    return obj;
  }

  onSubmit() {
    this.errorBody = undefined;
    this.genericError = false;
    const address: Address = this.removeUndefined({
      country: this.resolveFieldValueForIncludedField('address-country'),
      streetAddress: this.resolveFieldValueForIncludedField('address-streetAddress'),
      postalCode: this.resolveFieldValueForIncludedField('address-postalCode'),
      locality: this.resolveFieldValueForIncludedField('address-locality'),
      region: this.resolveFieldValueForIncludedField('address-region'),
      // formatted: this.resolveFieldValueForIncludedField('formattedAddress'),
    });
    const userToRegister: UserForRegisterUser = this.removeUndefined({
      email: this.resolveFieldValueForIncludedField('email'),
      firstName: this.resolveFieldValueForIncludedField('firstName'),
      lastName: this.resolveFieldValueForIncludedField('lastName'),
      password: this.resolveFieldValueForIncludedField('password'),
      phoneNumber: this.resolveFieldValueForIncludedField('phoneNumber'),
      preferredUsername: this.resolveFieldValueForIncludedField('preferredUsername'),
      nickname: this.resolveFieldValueForIncludedField('nickname'),
      professionalTitle: this.resolveFieldValueForIncludedField('professionalTitle'),
      address: address,
    });
    this.logger.debug('Registering...');
    const verificationUri = [];
    if (this.configuration.getProperties().registrationSendValidationEmail) {
      verificationUri.push(LocationUtil.getOwnDomainURL());
      verificationUri.push(getAppBaseHref());
      verificationUri.push(AppConstants.ROUTE_VALIDATE_EMAIL);
      if (verificationUri[0].indexOf('?') < 0) {
        verificationUri.push('?');
      } else {
        verificationUri.push('&');
      }
      verificationUri.push(AppConstants.QP_EMAIL_VALIDATION_KEY);
      verificationUri.push('={0}');
      verificationUri.push(
        LocationUtil.validParamsToQueryString(
          '&',
          [AppConstants.QP_EMAIL_VALIDATION_KEY, AppConstants.QP_EMAIL],
          this.configuration.getProperties().allowedOriginsForNextParam)
      );
      if (userToRegister.email) {
        verificationUri.push('&');
        verificationUri.push(AppConstants.QP_EMAIL);
        verificationUri.push('=');
        verificationUri.push(encodeURIComponent(userToRegister.email));
      }
    }
    this.appStateService.registerUser(
        userToRegister,
        verificationUri.length > 0,
        verificationUri.length > 0 ? verificationUri.join('') : undefined,
        undefined)
      .subscribe(response => {
        this.logger.debug('Registration completed, response %d', response.status);
        const t: ParamsForRouteNav|string = this.appStateService.getAppState().resolveNextRoute(
          AppConstants.AUHTENTICATION_PROCESS_QUERY_PARAMS)
        ;
        if (typeof t === 'string') {
          window.location.href = t as string;
        } else if (t) {
          this.router.navigate([t.path], {
            queryParams: t.queryParams,
            fragment: t.fragment,
          });
        }

      }, errorResponse => {
        this.logger.debug('Registration error, response %O', errorResponse);
        const challenges = ResponseUtil.readChallenges(errorResponse).required;
        let emailChallenge: AuthenticationChallenge = null;
        if (challenges && challenges.length) {
          emailChallenge = challenges.find(challenge => AppConstants.AC_AM_VERIFY_EMAIL === challenge.authenticationMethod);
        }
        if (emailChallenge) {
          this.logger.debug('Registration completed');
          // Email challenge is ok as it's handled in another component
          const t: ParamsForRouteNav|string = this.appStateService.getAppState().resolveNextRoute([AppConstants.QP_EMAIL]);
          if (typeof t === 'string') {
            window.location.href = t as string;
          } else if (t) {
            this.router.navigate([t.path], {
              queryParams: {
                ...t.queryParams,
                [AppConstants.QP_EMAIL]: userToRegister.email
              },
              fragment: t.fragment,
            });
          }
        } else if (errorResponse && errorResponse.error) {
          this.errorBody = errorResponse.error;
          this.genericError = true;
        }
      })
    ;
  }

  resolveCountryOptions(obj?: Item): Item[] {
    let t = null;
    if (this.countryList) {
      t = [].concat(this.countryList);
      if (obj) {
        t.unshift(obj);
      }
    }
    return t;
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputWrapperClass(): string {
    return FormInputComponent.WRAPPER_CLASS;
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputLabelClass(): string {
    return FormInputComponent.LABEL_CLASS;
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputControlWrapperClass(): string {
    return FormInputComponent.CONTROL_WRAPPER_CLASS;
  }

  private subscribeParamsState() {
    this.route.queryParams.subscribe(params => {
      const keyEmailParams = params[AppConstants.QP_EMAIL];
      let email;
      if (keyEmailParams) {
        if (Array.isArray(keyEmailParams)) {
          email = keyEmailParams[0];
        } else {
          email = keyEmailParams;
        }
      }
      this.email = email;
      if (this.email) {
        this.registerForm.get('email').setValue(this.email);
      }
      if (params[AppConstants.QP_INVITATION_TOKEN] &&
          params[AppConstants.QP_REQUIRE_CHALLENGE] === AppConstants.AC_AM_INVITATION_ACCEPTED &&
          this.email
      ) {
        this.emailAsReadOnly = true;
      }
    });
  }

  private validateConfirmPassword(control: AbstractControl): ValidationErrors | null {
    const pwField = this.registerForm.get('password');
    const cpwField = this.registerForm.get('confirmPassword');
    return cpwField.value === pwField.value
      ? null
      : { 'mustMatch': true };
  }
  public resolveSelectCountryLabel(): any {
    return this.translate.instant(_('register.country.select-option'));
  }
  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get('register.window-title');
  }
}
